import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom';
import { AxiosRequestConfig } from 'axios';
import * as _ from 'lodash';
import { isEqual, isNumber } from 'lodash';

const vendor: string = ((navigator && navigator.vendor) || '').toLowerCase();
const userAgent: string = ((navigator && navigator.userAgent) || '').toLowerCase();

export const isChrome = (): boolean => (/google inc/.test(vendor) ? userAgent.match(/(?:chrome|crios)\/(\d+)/) : null) !== null;
export const isEdge = (): boolean => userAgent.match(/edge\/(\d+)/) !== null;
export const isIe = (): boolean => userAgent.match(/(?:msie |trident.+?; rv:)(\d+)/) !== null;
export const isFirefox = (): boolean => userAgent.match(/(?:firefox|fxios)\/(\d+)/) !== null;
export const isOpera = (): boolean => userAgent.match(/(?:^opera.+?version|opr)\/(\d+)/) !== null;
export const isOperaMini = (): boolean => userAgent.match(/opera mini\/(\d+)/) !== null;
export const isSafari = (): boolean => userAgent.match(/version\/(\d+).+?safari/) !== null;

export const getBrowser = (): string | null => {
  let match: string | null;

  isChrome() ? match = 'chrome' :
    isEdge() ? match = 'edge' :
      isIe() ? match = 'ie' :
        isFirefox() ? match = 'firefox' :
          isOpera() ? match = 'opera' :
            isOperaMini() ? match = 'opera_mini' :
              isSafari() ? match = 'safari' :
                match = null;

  if (match === null) {
    console.warn('Cannot detect browser');
  }

  return match;
};

/**
 * Omit functions from object
 * @param {Object} input
 */
export const omitFunctions = (input: {}): void => {
  for (let key of Object.keys(input)) {
    if (typeof input[key] === 'function') {
      delete input[key];
    }
    if (typeof input[key] === 'object') {
      omitFunctions(input[key]);
    }
  }
};

/**
 * Omit functions from two objects and check if two objects are the same
 * @param {Object} obj1
 * @param {Object} obj2
 */
export const isEqualWithoutFunctions = (obj1: {}, obj2: {}): boolean => {
  const cloneObj1 = _.cloneDeep(obj1);
  const cloneObj2 = _.cloneDeep(obj2);
  omitFunctions(cloneObj1);
  omitFunctions(cloneObj2);

  return _.isEqual(cloneObj1, cloneObj2);
};

/**
 * Is element not button, input or link
 * @param {string} nodeName
 */
export const isElNotLinkButtonOrInput = (nodeName: string): boolean => {
  const nodeType: string = nodeName?.toLowerCase();
  return !FOCUSABLE_ELEMENTS_WITHIN_TR.includes(nodeType);
};

/**
 * Is focused element has tab index attribute
 */
export const isFocusedElementHasTabIndex = (): boolean => !!document.activeElement.getAttribute('tabIndex');

/**
 * Checks if element is visible inside container
 * @param element
 * @param container
 */
export const isElScrolledIntoView = (element: any, container: any) => {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  // Only top of element should be visible and it returns true:
  return elementRect.top >= containerRect.top;
};

/**
 * Switch between tabs while scrolling
 * Finds currently visible section, and sets focus on tab in navigation according to that
 *
 * Component instance should have:
 *  wrappersOfSections global variable that is refs[] of all sections
 *  scrollableContainer global variable that is ref to scrollable container of all sections
 * Component state should have:
 *  selectedSection string that is name of currently selected section
 *  preventTabSwitchOnScroll boolean variable that should be set to true only trough click handlers of tabs that scrolls to any section
 */
export const switchSectionTabOnScroll = (component: any) => {
  const { wrappersOfSections, scrollableContainer, state } = component;
  const { selectedSection, preventTabSwitchOnScroll } = state;
  let currentSectionIsScrolled = false;
  let currentSection;
  let i = 0;
  while (!currentSectionIsScrolled && i < wrappersOfSections.length) {
    currentSection = wrappersOfSections[i];
    currentSectionIsScrolled = isElScrolledIntoView(currentSection.ref, scrollableContainer);
    i++;
  }
  // Since section before last section is often visible even though page is scrolled to the bottom, while loop above will never check for the last section
  // added additional condition to check if container is scrolled to the bottom, and in that case select last section
  if (scrollableContainer.scrollHeight - scrollableContainer.scrollTop === scrollableContainer.clientHeight) {
    currentSectionIsScrolled = true;
    currentSection = wrappersOfSections[wrappersOfSections.length - 1];
  }
  const sectionName = currentSection.name;
  if (currentSectionIsScrolled) {
    if (!preventTabSwitchOnScroll && sectionName !== selectedSection) {
      component.setState({ selectedSection: sectionName });
    } else if (preventTabSwitchOnScroll && sectionName === selectedSection) {
      component.setState({ preventTabSwitchOnScroll: false });
    }
  }
};

/**
 * Sort objects array by property
 */
export const sortByProperty = (arr: any[], prop: string) => arr.sort((objectA: any, objectB: any) => objectA[prop] > objectB[prop] ? 1 : -1);

/**
 * Returns unix date regardless is it passed value date or unix date already. Exception is undefined passed value, in that case returns null
 *
 * @param date
 */
export const getUnixDate = function (date?: any) {
  let unixDate = null;
  if (_.isDate(date)) {
    unixDate = date.getTime();
  } else if (_.isNumber(date)) {
    unixDate = date;
  }
  return unixDate;
};

/**
 * Returns elements of arr1 that are not present in arr2
 */
export const diffArray = function (arr1: any[] = [], arr2: any[] = []) {
  return arr1.filter((item: any) => arr2.indexOf(item) < 0);
};

/**
 * Returns array of exponential numbers, if binaryTasks's item is '1' use that array index as exponent with base 2.
 * @param binaryTasks
 */
export const getAvailableTaskIds = (binaryTasks: string[]): number[] => {
  let exponent = 0;
  let taskIds = [];
  let numberOfBinaryTasks = binaryTasks.length;

  while (exponent < numberOfBinaryTasks) {
    if (binaryTasks[exponent] === '1') {
      taskIds.push(Math.pow(2, exponent));
    }
    exponent++;
  }

  return taskIds;
};

/**
 * Converts passed number to binary reversed array.
 * @param taskId
 */
export const getBinaryReversed = (taskId: number): string[] => taskId.toString(2).split('').reverse();

/**
 * Empty dropdown list renderer
 */
export const noDataDropDownRender = (emptyText: string): ReactElement => (
  <div className={'tp-no-data-found'}>
    {emptyText}
  </div>
);

/**
 * Returns array of all available actions on selected records
 * @param records
 */
export const disableActions = (records: any[]): string[] => {
  let binaryTasksAndProcessing;
  let availableTaskIds: number[];
  let availableActions: string[] = [];
  let newActions;

  records.forEach((record: any) => {
    binaryTasksAndProcessing = getBinaryReversed(record.availableTasks);
    availableTaskIds = getAvailableTaskIds(binaryTasksAndProcessing);

    newActions = Array.isArray(record.tasks) ? record.tasks.filter((task: any) => {
      const isTaskAvailable: boolean = availableTaskIds.includes(task.taskId);
      return isTaskAvailable;
    }).map((item: any) => item.taskName) : [];

    availableActions = !_.isEmpty(availableActions) ? _.intersection(availableActions, newActions) : newActions;
  });

  return availableActions;
};

/**
 * Adds the new array to position (index) into array and returns changed array.
 * @param array Original array
 * @param newArray
 * @param index
 * @param count
 */
export const spliceArray = (array: any[], newArray: any[], index: number = 0, count: number = 0) => {
  let args = [index, count].concat(newArray);
  array.splice.apply(array, args as any);
  return array;
};

export const contains = (array: any[], item: any): boolean => array.indexOf(item) !== -1;

export const findBy = (array: any[], item: any | Function, fromIndex: number = 0): any => _.find(array, item, fromIndex);

export const insert = (array: any[], index: number, newArray: any[]): any[] => spliceArray(array, newArray, index);

export const remove = (array: any[], item: any): any[] => _.remove(array, item);
export const removeAt = (array: any[], index: number): any[] => spliceArray(array, [], index, 1);

export const updateArray = (array: any[], items: any[], index: number = 0) => {
  const arrLen = array.length;
  const itemsLen = items.length;
  if (index >= 0 && index + itemsLen <= arrLen) {
    const partOne = array.slice(0, index);
    const partThird = array.slice(index + itemsLen, arrLen);
    return _.concat(partOne, items, partThird);
  } else {
    error('Wrong index or items long', 'error');
  }
};

export const error = (message: string | string[], type: string = 'error'): void => {
  if (process.env.NODE_ENV !== 'production') {
    if (!_.isString(type) || !contains(['log', 'info', 'warn', 'error'], type)) {
      type = 'log';
    }
    const log = (console as any)[type as string];
    if (_.isArray(message)) {
      log.apply(console, message);
    } else {
      log(message);
    }
  }
};

export const messageWithColors = (message: string, secondColor: string = 'black', type: string = 'error'): void => {
  let colorBlack;
  const colorRed = 'color: red;';
  let msg: string | string[] = [message];
  let matched = message.match(/%c/g);
  if (matched && matched.length % 2 === 0) {
    let numberOfColoredWords = matched.length / 2;
    colorBlack = 'color: ' + secondColor + ';';

    while (numberOfColoredWords) {
      msg = msg.concat([colorBlack, colorRed]);
      --numberOfColoredWords;
    }
  } else {
    msg = `Missing one '%c'! Passed message "${message}" is not valid!`;
  }

  error(msg, type);
};

/**
 * Returns custom bind method that has option to change scope, append or prepend passed arguments.
 *
 * @param {any} scope
 * @param {Function} fn
 * @param {any[]} args
 * @param {Boolean} append
 */
export const attach = function (scope: any, fn: Function, args: any[] = [], append: boolean = true) {
  return function (...currentArgs: any[]) {
    const boundArgs = append ? currentArgs.concat(args) : args.concat(currentArgs);
    return fn.apply(scope, boundArgs);
  };
};

export const getSizeByByte = (size: number) => {
  let fileSize = '';
  const kB = 1000;
  if (size < kB) {
    fileSize = `${size} bytes`;
  } else if (size > kB && size < kB * kB) {
    fileSize = `${Math.round(size / kB * 10) / 10} kB`;
  } else if (size > kB * kB && size < kB * kB * kB) {
    fileSize = `${Math.round(size / (kB * kB) * 10) / 10} MB`;
  } else if (size > kB * kB * kB && size < kB * kB * kB * kB) {
    fileSize = `${Math.round(size / (kB * kB * kB) * 10) / 10} GB`;
  } else {
    fileSize = `${Math.round(size / (kB * kB * kB * kB) * 10) / 10} TB`;
  }
  return fileSize;
};

/**
 * Gets request timeout in seconds.
 * @param timeout
 */
export const getTimeOut = (timeout: number): number => timeout * 1000;

/**
 * Forms base url based on PORT.
 * @param PROTOCOL
 * @param DOMAIN
 * @param PORT
 * @param NAME
 */
export const getBaseUrl = (PROTOCOL: string, DOMAIN: string, PORT: string, NAME: string) => {
  let url = `${PROTOCOL}://${DOMAIN}`;
  if (PORT) {
    url += `:${PORT}`;
  }

  if (NAME) {
    url += `/${NAME}`;
  }
  return `${url}/`;
};

export const getApiConfig = (PROTOCOL, DOMAIN, PORT, NAME, REQUEST_TIMEOUT): AxiosRequestConfig => ({
  baseURL: getBaseUrl(PROTOCOL, DOMAIN, PORT, NAME),
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Headers': 'Content-Type'
  },
  validateStatus: (status: number) => status >= 200 && status < 300,
  timeout: getTimeOut(REQUEST_TIMEOUT)
});

export const getMergeUpdatePaths = (mergeDataObj: any, propItem = '', props = []): any => {
  for (let prop in mergeDataObj) {
    if (mergeDataObj.hasOwnProperty(prop)) {
      if (prop === 'bind') {
        getMergeUpdatePaths(mergeDataObj[prop], propItem, props);
      } else if (_.isString(mergeDataObj[prop]) || _.isNumber(mergeDataObj[prop]) || _.isBoolean(mergeDataObj[prop] || _.isEmpty(mergeDataObj[prop]))) {
        props.push(`${propItem}${propItem ? '.' : ''}${prop}`);
      } else {
        propItem = `${propItem}${propItem ? '.' : ''}${prop}`;
        getMergeUpdatePaths(mergeDataObj[prop], propItem, props);
      }
    }
  }
  return props;
};

export const getMergeUpdateObj = (updateModel: any, updateObj: any, obj: any) => {
  const mergeDataObj = { ...updateModel };
  _.merge(obj, updateObj);
  const mergeUpdateProps = getMergeUpdatePaths(mergeDataObj.update);
  mergeUpdateProps.forEach((prop) => {
    _.set(obj, prop, _.get(updateObj, prop));
  });
  return obj;
};

export const getNodesFromContainerFromType = (container, type) => {
  const nodes = [];
  let i;
  let j;
  let nodesChild;
  if (container && container.childNodes && container.childNodes.length > 0) {
    for (i = 0; i < container.childNodes.length; i++) {
      if (container.childNodes[i].nodeName.toLowerCase() == type) {
        nodes.push(container.childNodes[i]);
      }
      nodesChild = getNodesFromContainerFromType(container.childNodes[i], type);
      for (j = 0; j < nodesChild.length; j++) {
        nodes.push(nodesChild[j]);
      }
    }
  }
  return nodes;
};

export const getNodesFromContainerFromTypes = (container, types, attributes?: any[]) => {
  let nodes = [];
  let i;
  let nodesChild;
  if (container && container.childNodes && container.childNodes.length > 0) {
    for (i = 0; i < container.childNodes.length; i++) {
      if (types.includes(container.childNodes[i].nodeName.toLowerCase()) || hasNodeAnyAttributes(container.childNodes[i], attributes)) {
        nodes.push(container.childNodes[i]);
      }
      nodesChild = getNodesFromContainerFromTypes(container.childNodes[i], types, attributes);
      nodes = _.concat(nodes, nodesChild);
    }
  }
  return nodes;
};

export const hasNodeAnyAttributes = (node, attributes?: any[]) => {
  let found;
  if (!_.isEmpty(attributes)) {
    found = attributes.find((item) => {
      const isRightType = _.isUndefined(item.type) || node.nodeName.toLowerCase() === item.type.toLowerCase();
      const nodeValue = node.getAttribute ? node.getAttribute(item.name) : null;
      return isRightType && nodeValue === item.value || (_.isUndefined(item.value) && !_.isUndefined(nodeValue) && !_.isNull(nodeValue));
    });
  }
  return !!found;
};

export const isDescendant = (parent, child) => {
  let node = child.parentNode;
  while (node && node !== parent) {
    node = node.parentNode;
  }
  return node === parent;
};

export const setDataCy = (component: React.Component, selector: string, value: string) => {
  const element: Element | null | Text = ReactDOM.findDOMNode(component);

  if (element && 'querySelectorAll' in element) {
    const nodes: NodeListOf<Element> = element.querySelectorAll(selector);
    let node: Element;

    for (let i = nodes.length - 1; i > -1; i--) {
      node = nodes[i];
      if (!node.hasAttribute('data-cy')) {
        node.setAttribute('data-cy', value);
      }
    }
  }
};

/**
 * Default colorpicker color pallete
 **/
export const EDITOR_COLOR_PALETTE = {
  'rgba(0, 0, 0, 0)': 'no_color',
  'rgba(0, 0, 0, 1)': 'black',
  'rgba(255, 255, 255, 1)': 'white',
  'rgba(245, 245, 245, 1)': 'lightest_grey',
  'rgba(211, 211, 211, 1)': 'ash',
  'rgba(169, 169, 169, 1)': 'grey',
  'rgba(128, 128, 128, 1)': 'dark_grey',
  'rgba(105, 105, 105, 1)': 'charcoal',
  'rgba(34, 139, 34, 1)': 'green',
  'rgba(0, 128, 0, 1)': 'dark_green',
  'rgba(154, 205, 50, 1)': 'light_green',
  'rgba(0, 191, 255, 1)': 'light_blue',
  'rgba(65, 105, 225, 1)': 'violet',
  'rgba(70, 130, 180, 1)': 'blue',
  'rgba(0, 0, 128, 1)': 'indigo',
  'rgba(128, 0, 128, 1)': 'purple',
  'rgba(199, 21, 133, 1)': 'magenta',
  'rgba(255, 0, 0, 1)': 'red',
  'rgba(255, 140, 0, 1)': 'orange',
  'rgba(255, 215, 0, 1)': 'yellow'
};

/**
 * Default colorpicker color pallete for FormExamples
 **/
export const paletteData = {
  ['rgba(0, 0, 0, 0)']: 'transparent',
  ['rgba(0, 0, 0, 1)']: 'black',
  ['rgba(255, 255, 255, 1)']: 'white',
  ['rgba(245, 245, 245, 1)']: 'lightest_grey',
  ['rgba(211, 211, 211, 1)']: 'ash',
  ['rgba(169, 169, 169, 1)']: 'grey',
  ['rgba(128, 128, 128, 1)']: 'dark_grey',
  ['rgba(105, 105, 105, 1)']: 'charcoal',
  ['rgba(34, 139, 34, 1)']: 'green',
  ['rgba(0, 128, 0, 1)']: 'dark_green',
  ['rgba(154, 205, 50, 1)']: 'light_green',
  ['rgba(0, 191, 255, 1)']: 'light_blue',
  ['rgba(65, 105, 225, 1)']: 'violet',
  ['rgba(70, 130, 180, 1)']: 'blue',
  ['rgba(0, 0, 128, 1)']: 'indigo',
  ['rgba(128, 0, 128, 1)']: 'purple',
  ['rgba(199, 21, 133, 1)']: 'magenta',
  ['rgba(255, 0, 0, 1)']: 'red',
  ['rgba(255, 140, 0, 1)']: 'orange',
  ['rgba(255, 215, 0, 1)']: 'yellow'
};

/**
 * Returns the width and height of the window
 **/
export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
};

/**
 * Returns sorted data
 * @param {Object[]} data State data
 * @param {String} field Column field name (dataIndex)
 * @param {String} sortType There can be 'asc' or 'desc'
 */
export const getSortedData = (data: any[], field: string, sortType: string) => {
  data.sort((a, b) => (a[field] > b[field]) ? 1 : ((b[field] > a[field]) ? -1 : 0));
  if (sortType === 'desc') {
    data.reverse();
  }
  return data;
};

/**
 * List of props which come from PUZZLE_FIELD_PROPS interface
 **/
export const ABSTRACT_FIELD_OMITTED_PROPS = ['asField', 'medium', 'errorTxt', 'labelClassName', 'withValidation', 'className', 'label', 'validationType'];
/**
 * List of props which comes from LAYOUT_FIELD_PROPS interface
 **/
export const FIELD_OMITTED_PROPS = ['fieldClassName', 'fieldMandatory', 'fieldWithAdditionalEl', 'fieldSize'];
/**
 * List of props which comes from PUZZLE_COMPONENT_TOOLTIP_PROPS interface
 **/
export const TOOLTIP_OMITTED_PROPS = ['withTooltip', 'titleText', 'tooltipContent', 'tooltipPosition', 'tooltipAnchor', 'tooltipOpenDelay', 'tooltipClassName'];
/**
 * List of props which come from PUZZLE_PASSWORD_INPUT_PROPS interface
 **/
export const PASSWORD_FIELD_OMITTED_PROPS = ['copyButtonPosition', 'copyButtonProps', 'withShowPasswordIcon', 'togglePasswordVisibility', 'inputType', 'showCopy'];

export const FOCUSABLE_ELEMENTS_WITHIN_TR: string[] = ['button', 'a', 'input'];

/**
 * This method converts HTML special characters to their entity equivalents.
 * @param {string} htmlStr
 */
export const encodeHtmlCharacters = (htmlStr?: string) => {
  let encodedHTML = '';
  if (htmlStr && _.isFunction(htmlStr.replace)) {
    encodedHTML = htmlStr.replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  return encodedHTML;
};
/**
 * This method converts HTML entity equivalents to their special characters.
 * Opposite from method encodeHtmlCharacters
 * @param {string} htmlStr
 */
export const decodeHtmlCharacters = (htmlStr?: string) => {
  let decodedHTML = '';
  if (htmlStr && _.isFunction(htmlStr.replace)) {
    let document = new DOMParser().parseFromString(htmlStr, 'text/html');
    decodedHTML = document.documentElement.textContent;
  }
  return decodedHTML;
};

/**
 * Regarding of supplied type of search returns proper function for string search.
 * currently supports includes and startsWith.
 * @param {'prefixMatch' | 'fullText' | undefined} type
 */
export const getTextSearchMethod = (type: 'prefixMatch' | 'fullText' | undefined = 'prefixMatch'): 'includes' | 'startsWith' => type === 'fullText' ? 'includes' : 'startsWith';

/**
 * Handle tab and shift + tab keyboard navigation through the popup
 * @param {React.KeyboardEvent} event
 * @param {React.RefObject<HTMLDivElement | null>} contentRef
 */
export const popupMenuWrapperHandleTabKeyDown = (event: React.KeyboardEvent, contentRef: React.RefObject<HTMLDivElement | null>) => {
  const { shiftKey, altKey, ctrlKey, target } = event;

  const isShift = shiftKey && !(altKey || ctrlKey);
  const nodes = contentRef.current.querySelectorAll('[tabindex]:not([tabindex="-1"])');
  const elements: HTMLElement[] = Array.from((nodes as NodeListOf<HTMLElement>));
  let index = elements.indexOf(target as HTMLElement);

  if (index === -1 && isEqual(target, contentRef.current)) {
    index = 0;
  }

  const lastIndex = elements.length - 1;
  let focusIndex;

  if (isShift) {
    focusIndex = (index === 0) && lastIndex;
  } else {
    focusIndex = (index === lastIndex) && 0;
  }

  if (isNumber(focusIndex)) {
    event.preventDefault();
    elements[focusIndex].focus();
  }
};
